import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';


const BankTransactionPanel = ({ transactions, onTransactionSelect, selectedTransactions, onDragStart }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');

  const filteredTransactions = transactions?.filter(transaction => {
    const matchesSearch = transaction?.description?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
                         transaction?.amount?.toString()?.includes(searchTerm);
    const matchesFilter = filterStatus === 'all' || transaction?.status === filterStatus;
    return matchesSearch && matchesFilter;
  });

  const getStatusColor = (status) => {
    switch (status) {
      case 'matched': return 'bg-success/10 text-success';
      case 'unmatched': return 'bg-warning/10 text-warning';
      case 'discrepancy': return 'bg-error/10 text-error';
      default: return 'bg-muted text-muted-foreground';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'matched': return 'CheckCircle';
      case 'unmatched': return 'AlertCircle';
      case 'discrepancy': return 'AlertTriangle';
      default: return 'Circle';
    }
  };

  return (
    <div className="bg-card rounded-lg shadow-sm border border-border h-full flex flex-col">
      <div className="p-4 md:p-6 border-b border-border">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 bg-primary/10 rounded-md flex items-center justify-center">
            <Icon name="Building2" size={20} color="var(--color-primary)" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-heading font-semibold text-base md:text-lg">Bank Transactions</h3>
            <p className="caption text-muted-foreground">
              {filteredTransactions?.length} transactions
            </p>
          </div>
        </div>

        <div className="space-y-3">
          <Input
            type="search"
            placeholder="Search transactions..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e?.target?.value)}
          />
          
          <div className="flex gap-2 overflow-x-auto pb-2">
            {['all', 'unmatched', 'matched', 'discrepancy']?.map((status) => (
              <button
                key={status}
                onClick={() => setFilterStatus(status)}
                className={`
                  px-3 py-1.5 rounded-md text-sm font-medium whitespace-nowrap transition-smooth
                  ${filterStatus === status
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-muted text-muted-foreground hover:bg-muted/80'
                  }
                `}
              >
                {status?.charAt(0)?.toUpperCase() + status?.slice(1)}
              </button>
            ))}
          </div>
        </div>
      </div>
      <div className="flex-1 overflow-y-auto p-4 md:p-6 space-y-3">
        {filteredTransactions?.map((transaction) => (
          <div
            key={transaction?.id}
            draggable
            onDragStart={(e) => onDragStart(e, transaction, 'bank')}
            className={`
              p-4 rounded-lg border-2 transition-smooth cursor-move hover-lift
              ${selectedTransactions?.includes(transaction?.id)
                ? 'border-primary bg-primary/5' :'border-border bg-background hover:border-primary/30'
              }
            `}
            onClick={() => onTransactionSelect(transaction?.id)}
          >
            <div className="flex items-start justify-between gap-3 mb-2">
              <div className="flex-1 min-w-0">
                <p className="font-body font-medium text-sm md:text-base text-foreground line-clamp-2">
                  {transaction?.description}
                </p>
                <p className="caption text-muted-foreground mt-1">
                  {transaction?.date} • {transaction?.reference}
                </p>
              </div>
              <div className={`
                px-2 py-1 rounded-md flex items-center gap-1.5 flex-shrink-0
                ${getStatusColor(transaction?.status)}
              `}>
                <Icon name={getStatusIcon(transaction?.status)} size={14} />
                <span className="caption font-medium whitespace-nowrap">
                  {transaction?.status}
                </span>
              </div>
            </div>

            <div className="flex items-center justify-between">
              <span className={`
                data-text font-semibold text-base md:text-lg
                ${transaction?.type === 'credit' ? 'text-success' : 'text-error'}
              `}>
                {transaction?.type === 'credit' ? '+' : '-'}${Math.abs(transaction?.amount)?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </span>
              {transaction?.confidence && (
                <span className="caption text-muted-foreground">
                  {transaction?.confidence}% match
                </span>
              )}
            </div>
          </div>
        ))}

        {filteredTransactions?.length === 0 && (
          <div className="text-center py-12">
            <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
              <Icon name="Search" size={32} color="var(--color-muted-foreground)" />
            </div>
            <p className="font-body text-muted-foreground">No transactions found</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default BankTransactionPanel;